import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Button,
  Snackbar,
  Alert,
  FormControlLabel,
  Typography,
  Checkbox,
} from "@mui/material";
import {
  fonts,
  headingFonts,
  primaryButtonStyles,
  secondaryButtonStyles,
  selectStyles,
  textFieldStyles,
} from "../../../Styles/Styles";

export default function ServiceProviderModal({ open, onClose }) {
  const [services, setServices] = useState([]);
  const [subServices, setSubServices] = useState([]);
  const [selectedService, setSelectedService] = useState("");
  const [selectedSubService, setSelectedSubService] = useState("");
  const [businessDetails, setBusinessDetails] = useState("");
  const [contactInfo, setContactInfo] = useState({ email: "", phone: "" });
  const [address, setAddress] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState();

  useEffect(() => {
    // Fetch services from the API
    const fetchServices = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/parentServices`
        );
        const data = await response.json();
        setServices(data);
      } catch (error) {
        console.error("Error fetching services:", error);
      }
    };

    fetchServices();
  }, []);

  const handleServiceChange = async (event) => {
    setSelectedService(event.target.value);
    console.log(event.target.value);
    // Fetch sub-services for the selected service
    if (event.target.value) {
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/services/subservices/${event.target.value}`
      );
      const data = await response.json();
      setSubServices(data.subServices);
    } else {
      setSubServices([]);
      setSelectedSubService("");
    }
  };

  const handleSubServiceChange = (event, subServiceId) => {
    if (event.target.checked) {
      // Add sub-service to the array if it's checked
      setSelectedSubService((prev) => [...prev, subServiceId]);
    } else {
      // Remove sub-service from the array if it's unchecked
      setSelectedSubService((prev) => prev.filter((id) => id !== subServiceId));
    }
  };

  const handleSubmit = async () => {
    try {
      // Prepare form data
      const data = {
        businessDetails,
        contactInfo,
        address,
        selectedService,
        selectedSubService,
      };

      // Call the API to submit the data
      const response = await fetch(
        `${process.env.REACT_APP_API_SERVER_BASE_URL}/serviceProviders/submit`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`, // Add token if necessary
          },
          body: JSON.stringify(data),
        }
      );

      // Check if the response is ok
      if (response.ok) {
        // Handle success
        console.log("Data submitted successfully");
        setSnackbarOpen(true); // Open success snackbar
        setSnackbarMessage(
          "Your service provider application has been submitted successfully."
        );
      } else if (response.status === 400) {
        // Handle specific 400 error
        const errorData = await response.json();
        setSnackbarOpen(true);
        setSnackbarMessage(errorData.message); // Display the message from the backend
      } else {
        // Handle failure (other non-200 responses)
        console.error("Error submitting data:", response.statusText);
        setSnackbarOpen(true);
        setSnackbarMessage("An error occurred while submitting your data.");
      }
    } catch (error) {
      console.error("Error submitting data:", error);
      // Optionally, show a failure message
      setSnackbarOpen(true);
      setSnackbarMessage("An unexpected error occurred. Please try again.");
    } finally {
      // Close the modal after submission
      onClose();
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle sx={{ ...headingFonts }}>
          Become a Service Provider
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} mt={2}>
              <TextField
                label="Business Details"
                variant="outlined"
                fullWidth
                value={businessDetails}
                onChange={(e) => setBusinessDetails(e.target.value)}
                multiline
                rows={4}
                required
                sx={{ ...textFieldStyles }}
              />
            </Grid>

            {/* Service Dropdown */}
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ ...fonts }}>Service</InputLabel>
                <Select
                  value={selectedService}
                  onChange={handleServiceChange}
                  label="Service"
                  required
                  sx={{ ...selectStyles }}
                >
                  {services.map((service) => (
                    <MenuItem
                      key={service._id}
                      value={service._id}
                      sx={{ ...fonts }}
                    >
                      {service.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Sub-service Dropdown */}
            {subServices.length > 0 && (
              <Grid item xs={12} sm={12}>
                <FormControl fullWidth>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ ...headingFonts }}
                  >
                    Sub-services
                  </Typography>
                  {subServices.map((subService) => (
                    <FormControlLabel
                      key={subService._id}
                      control={
                        <Checkbox
                          checked={selectedSubService.includes(subService._id)}
                          onChange={(e) =>
                            handleSubServiceChange(e, subService._id)
                          }
                          name={subService.name}
                          sx={{
                            "&.Mui-checked": {
                              color: "var(--primary-color)", // Filled color when checked
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ ...fonts }}>
                          {subService.name}
                        </Typography>
                      }
                    />
                  ))}
                </FormControl>
              </Grid>
            )}

            {/* Contact Info */}
            <Grid item xs={12} sm={12}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                required
                value={contactInfo.email}
                onChange={(e) =>
                  setContactInfo({ ...contactInfo, email: e.target.value })
                }
                sx={{ ...textFieldStyles }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel sx={{ ...fonts }}>Country Code</InputLabel>
                <Select
                  label="Country Code"
                  value={contactInfo.countryCode}
                  onChange={(e) =>
                    setContactInfo({
                      ...contactInfo,
                      countryCode: e.target.value,
                    })
                  }
                  required
                  sx={{ ...selectStyles }}
                >
                  <MenuItem value="+1345" sx={{ ...fonts }}>
                    +1345 (Cayman Islands)
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Phone"
                variant="outlined"
                fullWidth
                value={contactInfo.phone}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only digits and limit to 7 characters
                  if (/^\d{0,7}$/.test(value)) {
                    setContactInfo({ ...contactInfo, phone: value });
                  }
                }}
                required
                sx={{ ...textFieldStyles }}
              />
            </Grid>

            {/* Address */}
            <Grid item xs={12}>
              <TextField
                label="Address"
                variant="outlined"
                fullWidth
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                sx={{ ...textFieldStyles }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            sx={{ mt: 1, mb: 1, ...secondaryButtonStyles }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{ mt: 1, mb: 1, ...primaryButtonStyles }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="success">
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}
